import { Storage } from '@ambire-common/interfaces/storage'
import { parse, stringify } from '@ambire-common/libs/richJson/richJson'
import { browser, isExtension } from '@web/constants/browserapi'

// IndexedDB-backed key/value store for non-extension web targets (mobile-web).
// localStorage caps at ~5MB per origin; the `phishing` blocklist alone busts
// that quota. IDB has no practical ceiling for our payloads. We keep the
// stored value shape (richJson string) so MainController can read/write
// transparently. localStorage stays the fallback when IDB is unavailable.
const IDB_NAME = 'zkx-mobile-web-storage'
const IDB_STORE = 'kv'
let idbReady: Promise<IDBDatabase | null> | null = null

const openIDB = (): Promise<IDBDatabase | null> => {
  if (idbReady) return idbReady
  if (typeof indexedDB === 'undefined') {
    idbReady = Promise.resolve(null)
    return idbReady
  }
  idbReady = new Promise((resolve) => {
    // No explicit version: opens the DB at WHATEVER version it currently has
    // (creating it at v1 if absent). A pinned `open(name, 1)` would throw
    // VersionError forever once the self-heal below bumps the DB past v1.
    const req = indexedDB.open(IDB_NAME)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE)
    }
    req.onsuccess = () => {
      const db = req.result
      if (db.objectStoreNames.contains(IDB_STORE)) return resolve(db)
      // Self-heal: an interrupted app reset (appReset.ts races a blocked
      // deleteDatabase against a timeout and reloads regardless) can leave the
      // DB existing WITHOUT the store. The version already matches, so
      // onupgradeneeded above never fires again and every transaction throws
      // NotFoundError forever — reopen one version up to force the upgrade and
      // recreate the store. (Page and worker may both take this path; the
      // second open finds the store already created and skips its upgrade.)
      const retry = indexedDB.open(IDB_NAME, db.version + 1)
      db.close()
      retry.onupgradeneeded = () => {
        const d = retry.result
        if (!d.objectStoreNames.contains(IDB_STORE)) d.createObjectStore(IDB_STORE)
      }
      retry.onsuccess = () => resolve(retry.result)
      retry.onerror = () => resolve(null)
    }
    req.onerror = () => resolve(null)
  })
  return idbReady
}

const idbGet = (key: string): Promise<string | undefined> =>
  openIDB().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(undefined)
        const tx = db.transaction(IDB_STORE, 'readonly')
        const req = tx.objectStore(IDB_STORE).get(key)
        req.onsuccess = () => resolve(req.result as string | undefined)
        req.onerror = () => resolve(undefined)
      })
  )

const idbSet = (key: string, value: string): Promise<boolean> =>
  openIDB().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(false)
        const tx = db.transaction(IDB_STORE, 'readwrite')
        tx.objectStore(IDB_STORE).put(value, key)
        tx.oncomplete = () => resolve(true)
        tx.onerror = () => resolve(false)
      })
  )

const idbRemove = (key: string): Promise<void> =>
  openIDB().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve()
        const tx = db.transaction(IDB_STORE, 'readwrite')
        tx.objectStore(IDB_STORE).delete(key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
      })
  )

const commonAsyncStorage: Storage = {
  get: async (key: string, defaultValue: any): Promise<any> => {
    const fromIdb = await idbGet(key)
    if (typeof fromIdb === 'string') return parse(fromIdb)
    const legacy = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null
    if (legacy) {
      // One-shot migration: lift the value out of localStorage so we stop
      // hitting the 5MB quota on subsequent writes.
      idbSet(key, legacy).then((ok) => {
        if (ok && typeof localStorage !== 'undefined') localStorage.removeItem(key)
      })
      return parse(legacy)
    }
    return defaultValue
  },
  set: async (key: string, value: any) => {
    const serialized = stringify(value)
    const ok = await idbSet(key, serialized)
    if (!ok && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(key, serialized)
      } catch {
        /* quota — drop write rather than crash UI */
      }
    }
    return null
  },
  remove: async (key: string) => {
    await idbRemove(key)
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key)
    return null
  }
}

const formatValue = (value: any, defaultValue?: any) => {
  try {
    return typeof value === 'string' ? parse(value) : value
  } catch (error) {
    return typeof value === 'string' ? value : defaultValue
  }
}

const get = async (key: string, defaultValue?: any) => {
  const res = await browser.storage.local.get(key)

  if (!res[key]) return defaultValue

  return formatValue(res[key])
}

const set = async (key: string, value: any): Promise<null> => {
  await browser.storage.local.set({
    [key]: typeof value === 'string' ? value : stringify(value)
  })
  return null
}

const remove = async (key: string): Promise<null> => {
  await browser.storage.local.remove([key])
  return null
}

const extensionAsyncStorage: Storage = { get, set, remove }

const asyncStorage: Storage = isExtension ? extensionAsyncStorage : commonAsyncStorage

const syncStorage = {
  get: (key: string, defaultValue?: any): any => {
    return localStorage.getItem(key) ?? defaultValue
  },
  set: (key: string, value: any) => {
    return localStorage.setItem(key, value)
  },
  remove: (key: string) => {
    return localStorage.removeItem(key)
  }
}

const syncSessionStorage = {
  get: (key: string, defaultValue?: any): any => {
    return sessionStorage.getItem(key) ?? defaultValue
  },
  set: (key: string, value: any) => {
    return sessionStorage.setItem(key, value)
  },
  remove: (key: string) => {
    return sessionStorage.removeItem(key)
  }
}

const secureStorage = {
  get: (key: string) => {
    console.warn(`Secure storage is not supported on web. Attempted to get key: ${key}`)
    return Promise.resolve(null)
  },
  set: (key: string) => {
    console.warn(`Secure storage is not supported on web. Attempted to set key: ${key}`)
    return Promise.resolve()
  },
  remove: (key: string) => {
    console.warn(`Secure storage is not supported on web. Attempted to remove key: ${key}`)
    return Promise.resolve()
  }
}

export { asyncStorage as storage, syncStorage, syncSessionStorage, secureStorage }
